"use client"

import { useEffect, useState } from "react"
import { HttpTypes } from "@medusajs/types"
import { getProductPrice } from "@lib/util/get-product-price"
import {
  getProductBrand,
  getProductCode,
  getProductModel,
  getProductSpecChips,
  getProductSummary,
} from "@lib/util/product-specs"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Thumbnail from "@modules/products/components/thumbnail"
import PreviewPrice from "@modules/products/components/product-preview/price"

type ProductShowcaseProps = {
  products: HttpTypes.StoreProduct[]
  region: HttpTypes.StoreRegion
}

const MIN_DELAY_MS = 5_000
const DELAY_RANGE_MS = 10_000

function Chevron({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
    >
      <path
        d={direction === "left" ? "m12.5 4-6 6 6 6" : "m7.5 4 6 6-6 6"}
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function ProductShowcase({
  products,
  region: _region,
}: ProductShowcaseProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused || products.length < 2) {
      return
    }

    const delay = MIN_DELAY_MS + Math.random() * DELAY_RANGE_MS
    const timeout = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % products.length)
    }, delay)

    return () => window.clearTimeout(timeout)
  }, [activeIndex, paused, products.length])

  const product = products[activeIndex]

  if (!product) {
    return null
  }

  const { cheapestPrice } = getProductPrice({ product })
  const brand = getProductBrand(product)
  const model = getProductModel(product)
  const code = getProductCode(product)
  const summary = getProductSummary(product)
  const chips = getProductSpecChips(product).slice(0, 4)

  const showPrevious = () => {
    setActiveIndex(
      (current) => (current - 1 + products.length) % products.length
    )
  }

  const showNext = () => {
    setActiveIndex((current) => (current + 1) % products.length)
  }

  return (
    <div
      className="relative"
      role="region"
      aria-label="Featured product showcase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setPaused(false)
        }
      }}
    >
      <article
        key={product.id}
        className="animate-fade-in-up overflow-hidden rounded-xl border border-vx-line bg-vx-surface shadow-sm small:grid small:min-h-[460px] small:grid-cols-2"
        aria-label={`${model}, product ${activeIndex + 1} of ${products.length}`}
      >
        <div className="min-h-0 bg-vx-elevated">
          <Thumbnail
            thumbnail={product.thumbnail}
            images={product.images}
            size="square"
            className="h-full min-h-[280px] rounded-none border-0 bg-vx-elevated shadow-none small:min-h-[460px]"
            imageClassName="object-contain p-8 small:p-12"
          />
        </div>

        <div className="flex flex-col justify-center p-6 xsmall:p-8 small:p-12">
          {brand && (
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.14em] text-vx-accent">
              {brand}
            </span>
          )}

          <h3 className="mt-3 font-mono text-2xl font-semibold leading-tight text-vx-ink xsmall:text-3xl small:text-4xl">
            {model}
          </h3>

          {code && (
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.1em] text-vx-muted">
              {code}
            </p>
          )}

          {summary && (
            <p className="mt-5 line-clamp-3 text-sm leading-6 text-vx-muted small:text-base small:leading-7">
              {summary}
            </p>
          )}

          {chips.length > 0 && (
            <ul className="mt-6 grid grid-cols-2 gap-2">
              {chips.map((chip) => (
                <li
                  key={`${chip.key}-${chip.value}`}
                  className="border border-vx-line bg-vx-elevated px-3 py-2"
                >
                  <span className="block font-mono text-[10px] uppercase tracking-[0.1em] text-vx-muted">
                    {chip.label}
                  </span>
                  <span className="mt-1 block text-xs font-medium text-vx-ink">
                    {chip.value}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-7 flex flex-wrap items-center gap-5">
            {cheapestPrice && (
              <div className="text-xl font-semibold tracking-tight text-vx-ink small:text-2xl">
                <PreviewPrice price={cheapestPrice} />
              </div>
            )}
            <LocalizedClientLink
              href={`/products/${product.handle}`}
              className="vx-btn-primary min-h-11"
            >
              View details
            </LocalizedClientLink>
          </div>
        </div>
      </article>

      {products.length > 1 && (
        <>
          <button
            type="button"
            onClick={showPrevious}
            aria-label="Previous featured product"
            className="absolute left-3 top-[140px] z-10 grid h-11 w-11 place-items-center rounded-full border border-vx-line bg-vx-surface/95 text-vx-ink shadow-sm transition-colors hover:border-vx-accent hover:text-vx-accent small:left-5 small:top-1/2 small:-translate-y-1/2"
          >
            <Chevron direction="left" />
          </button>
          <button
            type="button"
            onClick={showNext}
            aria-label="Next featured product"
            className="absolute right-3 top-[140px] z-10 grid h-11 w-11 place-items-center rounded-full border border-vx-line bg-vx-surface/95 text-vx-ink shadow-sm transition-colors hover:border-vx-accent hover:text-vx-accent small:right-5 small:top-1/2 small:-translate-y-1/2"
          >
            <Chevron direction="right" />
          </button>

          <div
            className="mt-4 flex flex-wrap justify-center gap-1"
            aria-label="Choose featured product"
          >
            {products.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-label={`Show product ${index + 1}: ${getProductModel(item)}`}
                aria-current={index === activeIndex ? "true" : undefined}
                className="grid h-11 w-11 place-items-center"
              >
                <span
                  className={
                    index === activeIndex
                      ? "h-2.5 w-6 rounded-full bg-vx-accent transition-all"
                      : "h-2.5 w-2.5 rounded-full bg-vx-line transition-all hover:bg-vx-muted"
                  }
                />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
